// src/components/ReportsDashboard.js

import React, { useRef, useMemo } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend, RadialBarChart, RadialBar, PolarAngleAxis } from 'recharts';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

// Helper component for permission denied message, styled with Tailwind CSS
const PermissionDenied = () => (
    <div className="p-6 text-center bg-gray-50 min-h-screen flex items-center justify-center">
        <div className="bg-white shadow-md rounded-lg max-w-md mx-auto p-8">
            {/* Using an inline SVG for the icon to avoid dependency issues */}
            <svg className="w-16 h-16 text-red-500 mx-auto mb-4" xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 16 16">
              <path d="M8.982 1.566a1.13 1.13 0 0 0-1.96 0L.165 13.233c-.457.778.091 1.767.98 1.767h13.713c.889 0 1.438-.99.98-1.767L8.982 1.566zM8 5c.535 0 .954.462.9.995l-.35 3.507a.552.552 0 0 1-1.1 0L7.1 5.995A.905.905 0 0 1 8 5zm.002 6a1 1 0 1 1 0 2 1 1 0 0 1 0-2z"/>
            </svg>
            <h2 className="text-2xl font-bold text-red-600 mb-2">Access Denied</h2>
            <p className="text-gray-600">You do not have the necessary permissions to view this page.</p>
        </div>
    </div>
);

// A revised color palette for a more professional look
const COLORS = ['#2563eb', '#16a34a', '#64748b', '#f59e0b', '#4f46e5', '#d97706'];
const UTILIZATION_COLOR = '#2563eb'; // Tailwind's blue-600

// Reusable component for displaying key statistics in a visually appealing card, styled with Tailwind CSS
const StatCard = ({ title, value, icon, currency = '', unit = '' }) => (
    <div className="bg-white shadow-md rounded-lg p-6 flex flex-col items-center justify-center text-center h-full">
         <div className={`flex items-center justify-center h-16 w-16 rounded-full mb-4 ${icon.bgColor}`}>
            {/* Using an inline SVG for the icon */}
            <svg className={`w-8 h-8 text-white`} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d={icon.path} />
            </svg>
        </div>
        <h5 className="text-sm font-medium text-gray-500 uppercase tracking-wider">{title}</h5>
        <p className="text-3xl font-bold text-gray-900 mt-1">
            {currency}{value}{unit}
        </p>
    </div>
);


function ReportsDashboard({
  financialReports,
  caseloadReports,
  performanceMetrics,
  insightsReports,
  userRole,
  // --- NEW: Props for date filtering ---
  reportDateRange,
  setReportDateRange,
}) {
  const dashboardRef = useRef(null);

  const permissions = useMemo(() => ({
      canViewPage: ['PARTNER', 'ASSOCIATE', 'BOARD_MEMBER', 'SUPERUSER'].includes(userRole),
      canViewFinancials: ['PARTNER', 'BOARD_MEMBER', 'SUPERUSER'].includes(userRole),
      canViewInsights: ['PARTNER', 'BOARD_MEMBER', 'SUPERUSER'].includes(userRole),
      canViewCaseload: userRole === 'PARTNER' || userRole === 'SUPERUSER',
      canViewPerformance: ['PARTNER', 'ASSOCIATE', 'SUPERUSER'].includes(userRole),
  }), [userRole]);

  // --- NEW: Handler for date changes ---
  const handleDateChange = (e) => {
    const { name, value } = e.target;
    setReportDateRange(prev => ({ ...prev, [name]: value }));
  };

  const revenueByCaseTypeData = insightsReports.revenue_by_case_type?.map(item => ({
    name: item.invoice__case__case_type || "Uncategorized",
    revenue: parseFloat(item.total_revenue)
  })) || [];

  const utilizationData = [{
      name: 'Utilization',
      value: insightsReports.utilization_rate || 0,
  }];

  const clientAcquisitionData = insightsReports.client_acquisition ? [
      { name: 'New Clients', value: insightsReports.client_acquisition.new_clients_last_year },
      { name: 'Recurring Clients', value: insightsReports.client_acquisition.recurring_clients_last_year },
  ] : [];
  
  const profitabilityData = insightsReports.profitability_per_case || [];
  const clvData = insightsReports.client_lifetime_value || [];

  const handleExportPDF = () => {
    const input = dashboardRef.current;
    if (!input) return;
    html2canvas(input, { scale: 2, useCORS: true }).then((canvas) => {
        const imgData = canvas.toDataURL('image/png');
        const pdf = new jsPDF('p', 'mm', 'a4');
        const pdfWidth = pdf.internal.pageSize.getWidth();
        const canvasWidth = canvas.width;
        const canvasHeight = canvas.height;
        const ratio = canvasWidth / canvasHeight;
        const pdfHeight = (pdfWidth - 20) / ratio;
        pdf.addImage(imgData, 'PNG', 10, 10, pdfWidth - 20, pdfHeight);
        pdf.save('LegalOps_Reports_Dashboard.pdf');
    });
  };

  const currencyTooltipFormatter = (value) => `P${value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  if (!permissions.canViewPage) {
    return <PermissionDenied />;
  }

  return (
    <div className="p-6 bg-gray-50 min-h-screen" ref={dashboardRef}>
      <div className="flex flex-wrap justify-between items-center mb-6 pb-4 border-b border-gray-200 gap-4">
        <h2 className="text-3xl font-bold text-gray-800">Reports Dashboard</h2>
        <div className="flex items-center gap-4">
            {/* --- NEW: Date filter inputs --- */}
            <div className="flex items-center gap-2">
                <label htmlFor="startDate" className="text-sm font-medium text-gray-700">From:</label>
                <input 
                    type="date" 
                    id="startDate"
                    name="startDate"
                    value={reportDateRange.startDate}
                    onChange={handleDateChange}
                    className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                />
            </div>
            <div className="flex items-center gap-2">
                <label htmlFor="endDate" className="text-sm font-medium text-gray-700">To:</label>
                <input 
                    type="date" 
                    id="endDate"
                    name="endDate"
                    value={reportDateRange.endDate}
                    onChange={handleDateChange}
                    className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                />
            </div>
            <button 
              className="bg-white text-gray-700 px-4 py-2 rounded-md shadow-sm border border-gray-300 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              onClick={handleExportPDF}
            >
              Export PDF
            </button>
        </div>
      </div>

      {permissions.canViewFinancials && (
        <div className="mb-8">
            <h3 className="text-2xl font-bold text-gray-700 mb-4">Financial Overview</h3>
            {/* --- MODIFIED: Grid layout to accommodate 5 cards --- */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6">
                <StatCard 
                    title="Total Invoiced" 
                    value={parseFloat(financialReports.total_invoiced_amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} 
                    icon={{ path: "M9 14l6-6-6-6", bgColor: 'bg-blue-600' }}
                    currency="P"
                />
                <StatCard 
                    title="Total Paid" 
                    value={parseFloat(financialReports.paid_invoices_amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    icon={{ path: "M5 13l4 4L19 7", bgColor: 'bg-green-600' }}
                    currency="P"
                />
                <StatCard 
                    title="Outstanding" 
                    value={parseFloat(financialReports.outstanding_invoices_amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    icon={{ path: "M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z", bgColor: 'bg-purple-600' }}
                    currency="P"
                />
                {/* --- NEW: WIP StatCard --- */}
                <StatCard 
                    title="Work-In-Progress (WIP)" 
                    value={parseFloat(financialReports.work_in_progress_amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    icon={{ path: "M19.428 15.428a2 2 0 00-1.049-.548l-1.875-.469a2 2 0 01-1.049-.548l-1.875-.469a2 2 0 00-2.098 1.098l-.938 1.875a2 2 0 01-3.468-.866l-.937-1.875a2 2 0 00-2.098-1.098l-1.875.469a2 2 0 01-1.049.548l-1.875.469a2 2 0 00-1.049.548A2 2 0 002 17.528V19a2 2 0 002 2h16a2 2 0 002-2v-1.472a2 2 0 00-2.572-1.999zM14 6a4 4 0 10-8 0 4 4 0 008 0z", bgColor: 'bg-orange-500' }}
                    currency="P"
                />
                <StatCard 
                    title="Avg. Invoice Age (DSO)" 
                    value={insightsReports.average_invoice_age_days || 0}
                    icon={{ path: "M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z", bgColor: 'bg-yellow-500' }}
                    unit=" days"
                />
            </div>
        </div>
      )}
      
      {permissions.canViewInsights && (
        <div className="mb-8">
          <h3 className="text-2xl font-bold text-gray-700 mb-4">Key Performance Insights</h3>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            <div className="bg-white shadow-md rounded-lg p-6 h-full">
              <h5 className="text-base font-semibold text-gray-700 mb-4">Revenue by Case Type</h5>
              <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={revenueByCaseTypeData} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" />
                      <YAxis />
                      <Tooltip formatter={currencyTooltipFormatter} />
                      <Legend />
                      <Bar dataKey="revenue" fill={COLORS[0]} />
                  </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="bg-white shadow-md rounded-lg p-6 h-full">
              <h5 className="text-base font-semibold text-gray-700 mb-4">Most Profitable Cases (Top 10)</h5>
              <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={profitabilityData} layout="vertical" margin={{ top: 5, right: 20, left: 120, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis type="number" />
                      <YAxis dataKey="case_title" type="category" width={120} />
                      <Tooltip formatter={currencyTooltipFormatter} />
                      <Legend />
                      <Bar dataKey="profit" fill={COLORS[4]} />
                  </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          <h3 className="text-2xl font-bold text-gray-700 mb-4">Productivity & Efficiency</h3>
           <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
             <div className="bg-white shadow-md rounded-lg p-6 flex flex-col items-center justify-center h-full">
                <h5 className="text-base font-semibold text-gray-700 mb-4">Firm Utilization Rate</h5>
                <ResponsiveContainer width="100%" height={250}>
                    <RadialBarChart innerRadius="80%" outerRadius="100%" data={utilizationData} startAngle={90} endAngle={-270}>
                        <PolarAngleAxis type="number" domain={[0, 100]} angleAxisId={0} tick={false} />
                        <RadialBar background clockWise dataKey='value' cornerRadius={10} fill={UTILIZATION_COLOR} />
                        <text x="50%" y="50%" textAnchor="middle" dominantBaseline="middle" fontSize="2rem" fontWeight="bold" fill={UTILIZATION_COLOR}>
                            {`${insightsReports.utilization_rate || 0}%`}
                        </text>
                    </RadialBarChart>
                </ResponsiveContainer>
            </div>
            <div className="lg:col-span-1">
              <StatCard 
                  title="Avg. Case Lifecycle" 
                  value={insightsReports.average_case_lifecycle_days || 0}
                  icon={{ path: "M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z", bgColor: 'bg-green-600' }}
                  unit=" days"
              />
            </div>
            <div className="bg-white shadow-md rounded-lg p-6 h-full lg:col-span-2">
              <h5 className="text-base font-semibold text-gray-700 mb-4">Client Acquisition (Last Year)</h5>
               <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie dataKey="value" data={clientAcquisitionData} outerRadius={100} label>
                        {clientAcquisitionData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
            </div>
          </div>
          
          <h3 className="text-2xl font-bold text-gray-700 mb-4">Client Analysis</h3>
          <div className="grid grid-cols-1 gap-6">
             <div className="bg-white shadow-md rounded-lg p-6 h-full">
                <h5 className="text-base font-semibold text-gray-700 mb-4">Client Lifetime Value (Top 10)</h5>
                <ResponsiveContainer width="100%" height={400}>
                    <BarChart data={clvData} layout="vertical" margin={{ top: 20, right: 30, left: 100, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis type="number" />
                        <YAxis dataKey="client_name" type="category" width={150} />
                        <Tooltip formatter={currencyTooltipFormatter} />
                        <Legend />
                        <Bar dataKey="total_revenue" name="Total Revenue" fill={COLORS[5]} />
                    </BarChart>
                </ResponsiveContainer>
            </div>
          </div>
        </div>
      )}

      {permissions.canViewCaseload && (
          <div className="mb-8">
            <h3 className="text-2xl font-bold text-gray-700 mb-4">Caseload Overview</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
              <StatCard 
                title="Total Cases" 
                value={caseloadReports.total_cases || 0}
                icon={{ path: "M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z", bgColor: 'bg-indigo-600' }}
              />
              <StatCard 
                title="Open/Active" 
                value={(caseloadReports.cases_by_status || []).filter(s => ['Open','Active','Pending','On_Hold'].includes(s.status)).reduce((a,b) => a + (b.count || 0), 0)}
                icon={{ path: "M5 13l4 4L19 7", bgColor: 'bg-teal-600' }}
              />
              <StatCard 
                title="Closed" 
                value={(caseloadReports.cases_by_status || []).filter(s => s.status === 'Closed').reduce((a,b) => a + (b.count || 0), 0)}
                icon={{ path: "M9 14l6-6m-5.5.5h.01m4.99 5h.01M19 14l-6-6", bgColor: 'bg-gray-600' }}
              />
              <StatCard 
                title="Avg. Days to Close" 
                value={caseloadReports.average_days_to_close_case || 0}
                icon={{ path: "M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z", bgColor: 'bg-amber-600' }}
                unit=" days"
              />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-white shadow-md rounded-lg p-6 h-full">
                <h5 className="text-base font-semibold text-gray-700 mb-4">Cases by Status</h5>
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={(caseloadReports.cases_by_status || []).map(item => ({ name: item.status, count: item.count }))}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis allowDecimals={false} />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="count" fill={COLORS[2]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <div className="bg-white shadow-md rounded-lg p-6 h-full">
                <h5 className="text-base font-semibold text-gray-700 mb-4">Cases by Type</h5>
                <ResponsiveContainer width="100%" height={280}>
                  <PieChart>
                    <Pie dataKey="count" data={(caseloadReports.cases_by_type || []).map((item, idx) => ({ name: item.case_type || 'Unspecified', count: item.count, fill: COLORS[idx % COLORS.length] }))} outerRadius={100} label>
                      {(caseloadReports.cases_by_type || []).map((_, index) => (
                        <Cell key={`case-type-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
      )}

      {permissions.canViewPerformance && (
          <div className="mb-8">
            <h3 className="text-2xl font-bold text-gray-700 mb-4">Performance Metrics</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                <StatCard 
                    title="Total Time Logged" 
                    value={performanceMetrics.total_time_logged_hours || 0}
                    icon={{ path: "M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z", bgColor: 'bg-cyan-500' }}
                    unit=" hrs"
                />
                {/* Add more charts for performanceMetrics */}
            </div>
          </div>
      )}
    </div>
  );
}

export default ReportsDashboard;
